/**
 * Dashboard Metrics Service
 * Fetches real employee metrics for dashboard display
 * Integrates with existing workforce and performance APIs
 */

import { get } from '@/api/lib/amplify-wrapper'
import { createImpersonatedApiHeaders, logApiCall } from './api-utils'
import type { VacationBalanceResponse, VacationBalance } from '@/types/vacation'
import type { IndividualProgressResponse, OKR } from '@/types/performance'

// ============================================================================
// TYPES
// ============================================================================

export interface EmployeeDashboardMetrics {
  vacation: VacationMetrics | null
  training: TrainingMetrics | null
  bancoDeHoras: BancoDeHorasMetrics | null
  nextEvaluation: EvaluationMetrics | null
  development: DevelopmentMetrics | null
}

export interface VacationMetrics {
  available: number
  accrued: number
  used: number
  expiringCount?: number
  expiringDate?: string
}

export interface TrainingMetrics {
  completed: number
  inProgress: number
  required: number
}

export interface BancoDeHorasMetrics {
  balance: string // e.g., "+8h" or "-2h"
  balanceMinutes: number
}

export interface EvaluationMetrics {
  date: string
  type: string
}

export interface DevelopmentMetrics {
  skills: Array<{
    name: string
    progress: number
    lastUpdated: string
  }>
}

// ============================================================================
// API HELPERS
// ============================================================================

const workforceApiCall = async (endpoint: string, params: Record<string, string> = {}): Promise<any> => {
  const queryParams = new URLSearchParams(params)
  const path = `/workforce${endpoint}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

  logApiCall(path)

  const { response } = await get({
    apiName: 'workforce',
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const result = await response
  return await result.body.json()
}

const performanceApiCall = async (endpoint: string, params: Record<string, string> = {}): Promise<any> => {
  const queryParams = new URLSearchParams(params)
  const path = `/performance${endpoint}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

  logApiCall(path)

  const { response } = await get({
    apiName: 'performance',
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const result = await response
  return await result.body.json()
}

// ============================================================================
// VACATION DATA
// ============================================================================

/**
 * Fetch vacation balance for current user or specific employee
 */
export async function getVacationMetrics(employeeId?: string): Promise<VacationMetrics | null> {
  try {
    const params: Record<string, string> = employeeId ? { employeeId } : {}
    const response: VacationBalanceResponse = await workforceApiCall('/vacation/balance', params)

    if (!response.balances || response.balances.length === 0) {
      return null
    }

    // Get the most recent balance period
    const latestBalance = response.balances.sort((a, b) =>
      new Date(b.acquisitionStartDate).getTime() - new Date(a.acquisitionStartDate).getTime()
    )[0]

    return {
      available: latestBalance.daysAvailable,
      accrued: latestBalance.daysEarned,
      used: latestBalance.daysUsed,
      expiringCount: latestBalance.isExpiringSoon ? latestBalance.daysAtRisk : undefined,
      expiringDate: latestBalance.isExpiringSoon ? latestBalance.expirationDate : undefined
    }
  } catch (error) {
    console.error('[dashboard-metrics] Error fetching vacation data:', error)
    return null
  }
}

// ============================================================================
// PERFORMANCE/DEVELOPMENT DATA
// ============================================================================

/**
 * Fetch individual progress data for current user
 */
export async function getDevelopmentMetrics(employeeId?: string): Promise<DevelopmentMetrics | null> {
  try {
    const endpoint = employeeId
      ? `/dashboard/individual-progress/${employeeId}`
      : '/dashboard/individual-progress/me'

    const response: IndividualProgressResponse = await performanceApiCall(endpoint)

    if (!response.data || !response.data.activeOKRs || response.data.activeOKRs.length === 0) {
      return null
    }

    // Extract skills from active OKRs
    // Use OKR objective as the skill name and progress as the percentage
    const skills: Array<{ name: string; progress: number; lastUpdated: string }> = []

    response.data.activeOKRs.forEach((okr: OKR) => {
      skills.push({
        name: okr.objective,
        progress: Math.round(okr.progress),
        lastUpdated: okr.updatedAt
      })
    })

    return { skills }
  } catch (error) {
    console.error('[dashboard-metrics] Error fetching development data:', error)
    return null
  }
}

/**
 * Fetch next evaluation date from performance data
 */
export async function getNextEvaluationMetrics(employeeId?: string): Promise<EvaluationMetrics | null> {
  try {
    const endpoint = employeeId
      ? `/dashboard/individual-progress/${employeeId}`
      : '/dashboard/individual-progress/me'

    const response: IndividualProgressResponse = await performanceApiCall(endpoint)

    if (!response.data || !response.data.upcomingMeetings || response.data.upcomingMeetings.length === 0) {
      return null
    }

    // Find the next evaluation meeting (if any)
    const now = new Date()
    const upcomingEvaluations = response.data.upcomingMeetings
      .filter(meeting => {
        const meetingDate = new Date(meeting.date)
        return meetingDate > now
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())

    if (upcomingEvaluations.length === 0) {
      return null
    }

    const nextEvaluation = upcomingEvaluations[0]
    return {
      date: nextEvaluation.date,
      type: 'Reunião 1:1'
    }
  } catch (error) {
    console.error('[dashboard-metrics] Error fetching evaluation data:', error)
    return null
  }
}

// ============================================================================
// TRAINING DATA (Not yet available in API)
// ============================================================================

/**
 * Fetch training metrics - Currently not available in API
 * Returns null until training endpoint is implemented
 */
export async function getTrainingMetrics(employeeId?: string): Promise<TrainingMetrics | null> {
  // TODO: Implement when training API is available
  // Expected endpoint: GET /workforce/training/me or /workforce/training/:employeeId
  return null
}

// ============================================================================
// BANCO DE HORAS (Not yet available in API)
// ============================================================================

/**
 * Fetch banco de horas metrics - Currently not available in API
 * Returns null until time tracking endpoint is implemented
 */
export async function getBancoDeHorasMetrics(employeeId?: string): Promise<BancoDeHorasMetrics | null> {
  // TODO: Implement when time tracking API is available
  // Expected endpoint: GET /workforce/timetracking/balance/:employeeId or /workforce/timetracking/balance/me
  return null
}

// ============================================================================
// COMBINED METRICS
// ============================================================================

/**
 * Fetch all dashboard metrics for an employee
 * Returns real data where available, null for unavailable metrics
 */
export async function getEmployeeDashboardMetrics(employeeId?: string): Promise<EmployeeDashboardMetrics> {
  try {
    // Fetch all metrics in parallel for performance
    const [vacation, development, nextEvaluation, training, bancoDeHoras] = await Promise.all([
      getVacationMetrics(employeeId),
      getDevelopmentMetrics(employeeId),
      getNextEvaluationMetrics(employeeId),
      getTrainingMetrics(employeeId),
      getBancoDeHorasMetrics(employeeId)
    ])

    return {
      vacation,
      training,
      bancoDeHoras,
      nextEvaluation,
      development
    }
  } catch (error) {
    console.error('[dashboard-metrics] Error fetching employee metrics:', error)
    // Return all null metrics on error
    return {
      vacation: null,
      training: null,
      bancoDeHoras: null,
      nextEvaluation: null,
      development: null
    }
  }
}
